import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { AdminGetAllAsonePlansPlanResponseDto } from 'app/API';
import { StyledUniversalNoPictureCard } from './styles/StyledUniversalNoPictureCard';

type TProps = {
  plan: AdminGetAllAsonePlansPlanResponseDto;
  onChange: () => void;
};

export const Plan = ({ plan, onChange }: TProps) => {
  const { openModal } = useModalContext();
  const { me } = useAuthContext();
  const isVsBilling = me?.roles?.includes('ROLE_BILLING_ADMIN');
  if (!plan) {
    return <></>;
  }

  const onEdit = () => {
    if (!isVsBilling) {
      return;
    }
    openModal(ModalNamesEnum.EditASOnePlan, {
      plan,
      onSuccess: () => onChange(),
    });
  };

  const isProduction = window.location.hostname.split('.')[0] === 'www';

  const content = (
    <>
      <p className="font-medium text-[var(--color-title)]">{plan.name}</p>
      <p className="text-foreground-muted text-xs">Realtime ASR: {plan.realtimeASREnabled ? 'Enabled' : 'Disabled'}</p>
      <p className="text-foreground-muted text-xs">${plan.price} per license / month</p>
      {plan.DefaultBillingConfiguration ? (
        <>
          {plan.DefaultBillingConfiguration.realTimeRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Realtime / ASR rate: ${plan.DefaultBillingConfiguration.realTimeRate} (
              {plan.DefaultBillingConfiguration.realTimeRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Realtime / ASR rate: {plan.DefaultBillingConfiguration.realTimeRateType}
            </p>
          )}
          {plan.DefaultBillingConfiguration.postProcessingRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Post rate: ${plan.DefaultBillingConfiguration.postProcessingRate} (
              {plan.DefaultBillingConfiguration.postProcessingRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Post rate: {plan.DefaultBillingConfiguration.postProcessingRateType}
            </p>
          )}
          {plan.DefaultBillingConfiguration.partialUnlockRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Reporter Unlock rate: ${plan.DefaultBillingConfiguration.partialUnlockRate} (
              {plan.DefaultBillingConfiguration.partialUnlockRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Reporter Unlock rate: {plan.DefaultBillingConfiguration.partialUnlockRateType}
            </p>
          )}
          {plan.DefaultBillingConfiguration.fullUnlockRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Full Unlock rate: ${plan.DefaultBillingConfiguration.fullUnlockRate} (
              {plan.DefaultBillingConfiguration.fullUnlockRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Full Unlock rate: {plan.DefaultBillingConfiguration.fullUnlockRateType}
            </p>
          )}
          {plan.DefaultBillingConfiguration.enhancedOnDemandRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Enhance on Demand rate: ${plan.DefaultBillingConfiguration.enhancedOnDemandRate} (
              {plan.DefaultBillingConfiguration.enhancedOnDemandRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Enhance on Demand rate: {plan.DefaultBillingConfiguration.enhancedOnDemandRateType}
            </p>
          )}

          {plan.DefaultBillingConfiguration.attorneyConnectFeeRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Attorney Connect Fee rate: ${plan.DefaultBillingConfiguration.attorneyConnectFeeRate} (
              {plan.DefaultBillingConfiguration.attorneyConnectFeeRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Attorney Connect Fee rate: {plan.DefaultBillingConfiguration.attorneyConnectFeeRateType}
            </p>
          )}

          {plan.DefaultBillingConfiguration.attorneyInviteFeeRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Attorney Invite Fee rate: ${plan.DefaultBillingConfiguration.attorneyInviteFeeRate} (
              {plan.DefaultBillingConfiguration.attorneyInviteFeeRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Attorney Invite Fee rate: {plan.DefaultBillingConfiguration.attorneyInviteFeeRateType}
            </p>
          )}

          {plan.DefaultBillingConfiguration.rushLabelFeeRateType !== 'TIER_RATE' ? (
            <p className="text-foreground-muted text-xs">
              Rush Label Fee rate: ${plan.DefaultBillingConfiguration.rushLabelFeeRate} (
              {plan.DefaultBillingConfiguration.rushLabelFeeRateType})
            </p>
          ) : (
            <p className="text-foreground-muted text-xs">
              Rush Label Fee rate: {plan.DefaultBillingConfiguration.rushLabelFeeRateType}
            </p>
          )}
        </>
      ) : null}
      <p className="text-foreground-muted text-xs">Free trial days: {plan.trialPeriodDays}</p>
      <p className="text-foreground-muted text-xs">
        Hours per month: {plan.unlimitedHoursPerMonth ? 'Unlimited' : plan.audioHoursPerMonth}
      </p>
      <p className="text-foreground-muted text-xs">Free seats: {plan.freeSeats}</p>
      <p className="text-foreground-muted text-xs">{plan.public ? 'Public' : 'Not public'}</p>
      {plan.allowedToChooseTranscriptUnlockType ? (
        <p className="text-foreground-muted text-xs">Allowed to choose Transcript Unlock Type</p>
      ) : null}

      <hr className="my-4" />
      <details>
        <summary className="text-foreground-muted text-xs">Stripe details</summary>
        <p className="text-foreground-muted text-xs">
          Plan ID:{' '}
          <a
            href={`https://dashboard.stripe.com/${isProduction ? '' : 'test/'}products/${plan.stripeProductId}`}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            {plan.stripeProductId}
          </a>
        </p>
        <p className="text-foreground-muted text-xs">
          Price ID:{' '}
          <a
            href={`https://dashboard.stripe.com/${isProduction ? '' : 'test/'}prices/${plan.stripeItemPriceId}`}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            {plan.stripeItemPriceId}
          </a>
        </p>
      </details>
    </>
  );

  const actions = isVsBilling ? [{ label: 'Edit', onClick: onEdit }] : [];

  return <StyledUniversalNoPictureCard className="plan-card" content={content} actions={actions} />;
};
